// 2026-10-28 · Conexión de restaurantSession.js con la app: store, axios,
// hubs y avisos. Ver la explicación en restaurantSession.js.
//
//  - startRestaurantSessionRenewal(): una vez al montar la app (JwtProvider).
//  - resolveRestaurantUnauthorized(error): lo llama el interceptor de axios
//    ante un 401 del restaurante; devuelve la petición a repetir (con el token
//    renovado) o null (la sesión ha terminado o no aplica).
import { store } from "src/redux/store";
import { showSnackbar } from "./globalSnackbar";
import {
  CHECK_INTERVAL_MS,
  DEVICE_SESSION_CLOSED_CODE,
  FIRST_CHECK_DELAY_MS,
  createExpiryOnce,
  createRestaurantSessionRenewal,
  expiredMessageFor,
  isRenewalUrl,
  rememberExpiredNotice,
  retryConfigWithToken,
  sentBearerToken,
} from "./restaurantSession";

// Imports perezosos: axios.js importa este módulo de forma diferida y
// authJwt importa axios; así no hay ciclos al arrancar.
// eslint-disable-next-line global-require
const getAxios = () => require("./axios").default;
// eslint-disable-next-line global-require
const getHubManager = () => require("./hubs/HubConnectionManager");
// eslint-disable-next-line global-require
const getAuthSlice = () => require("src/redux/slices/authJwt");
// eslint-disable-next-line global-require
const getHubSlice = () => require("src/redux/slices/hub");

/** Hay sesión de RESTAURANTE (no comensal QR ni página pública). */
function hasRestaurantSession() {
  try {
    if (store.getState()?.authJwt?.isAuthenticated !== true) return false;
    const token = window.localStorage.getItem("accessToken");
    const user = window.localStorage.getItem("userId");
    return !!token && token !== "null" && !!user && user !== "null" && user !== "undefined";
  } catch {
    return false;
  }
}

function endRestaurantSession(code) {
  try { getHubManager().closeAllConnections(); } catch { /* ignore */ }
  const message = expiredMessageFor(code);
  rememberExpiredNotice(code);
  showSnackbar(message, {
    variant: "warning",
    key: "restaurant-session-expired",
    preventDuplicate: true,
    autoHideDuration: 10000,
  });
  try { store.dispatch(getHubSlice().cleanAll()); } catch { /* ignore */ }
  // Igual que «Cerrar sesión»: hubs cerrados, token y sesión de empleado
  // fuera, isAuthenticated=false → AuthProtect lleva al login.
  try { store.dispatch(getAuthSlice().logout()); } catch { /* ignore */ }
}

const expireOnce = createExpiryOnce({ run: endRestaurantSession });

/** Cierra la sesión del restaurante con UN aviso (no-op si ya no hay sesión). */
export function expireRestaurantSession(code = null) {
  if (!hasRestaurantSession()) return false;
  return expireOnce(code);
}

/** Solo tests. */

const renewal = createRestaurantSessionRenewal({
  storage: typeof window !== "undefined" ? window.localStorage : null,
  isActive: hasRestaurantSession,
  renew: () => store.dispatch(getAuthSlice().renewRestaurantToken()),
  adoptToken: (token) => {
    getAxios().defaults.headers.common.Authorization = `Bearer ${token}`;
  },
  onExpired: (code) => expireRestaurantSession(code),
});

export const restaurantSessionRenewal = renewal;

/** Solo tests. */
export function __resetRestaurantSessionExpiry() {
  expireOnce.reset();
  renewal.reset();
}

/** Ante un 401 del restaurante: config a repetir o null. */
export async function resolveRestaurantUnauthorized(error) {
  const cfg = error?.config;
  if (!cfg || cfg.__restaurantAuthRetried || !hasRestaurantSession()) return null;
  if (error?.response?.data?.code === DEVICE_SESSION_CLOSED_CODE) {
    expireRestaurantSession(DEVICE_SESSION_CLOSED_CODE);
    return null;
  }
  // La petición salió con un token que ya no es el vigente (renovado aquí o
  // en otra pestaña): se repite con el actual.
  const sent = sentBearerToken(cfg);
  const current = renewal.readToken();
  if (current && sent && sent !== current) return retryConfigWithToken(cfg, current);
  // La propia renovación (p. ej. al arrancar) rechazada → no hay nada que renovar.
  if (isRenewalUrl(cfg.url)) {
    expireRestaurantSession(null);
    return null;
  }
  const outcome = await renewal.recover();
  if (outcome !== "renewed") return null;
  const fresh = renewal.readToken();
  return fresh ? retryConfigWithToken(cfg, fresh) : null;
}

let started = false;

/** Arranca la renovación silenciosa (idempotente). */
export function startRestaurantSessionRenewal() {
  if (started || typeof window === "undefined") return;
  started = true;
  const check = () => { renewal.check().catch(() => {}); };

  setTimeout(check, FIRST_CHECK_DELAY_MS);
  setInterval(check, CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.addEventListener("focus", check);
  window.addEventListener("online", check);
  window.addEventListener("storage", (e) => renewal.onStorage(e));

  try {
    getHubManager().setHubStartFailureObserver((info) => {
      renewal.onHubStartFailure(info).catch(() => {});
    });
  } catch { /* ignore */ }
}
